import { createSlice } from "@reduxjs/toolkit";

import type { PayloadAction } from "@reduxjs/toolkit";

export interface Warning {
  id: string;
  type: string;
  identified: string; // ISO date string
  message: string;
  seen: boolean;
}

interface WarningsState {
  warningsList: Warning[];
  isOpen: boolean;
  lastOpenedAt: string | null; // ISO date string
  unreadCount: number;
}

// Get last opened from localStorage
const getLastOpenedFromStorage = (): string | null => {
  try {
    return localStorage.getItem("warningsLastOpened");
  } catch {
    return null;
  }
};

const initialState: WarningsState = {
  warningsList: [],
  isOpen: false,
  lastOpenedAt: getLastOpenedFromStorage(),
  unreadCount: 0,
};

const warningsSlice = createSlice({
  name: "warnings",
  initialState,
  reducers: {
    addWarning(state, action: PayloadAction<Warning>) {
      // Add warning to the beginning of the list
      state.warningsList.unshift(action.payload);
      // Update unread count
      state.unreadCount = state.warningsList.filter((w) => !w.seen).length;
    },
    setWarningsList(state, action: PayloadAction<Warning[]>) {
      state.warningsList = action.payload;
      // Calculate unread count based on seen status
      state.unreadCount = action.payload.filter((w) => !w.seen).length;
    },
    markWarningSeen(state, action: PayloadAction<string>) {
      const warning = state.warningsList.find((w) => w.id === action.payload);
      if (warning) {
        warning.seen = true;
        state.unreadCount = state.warningsList.filter((w) => !w.seen).length;
      }
    },
    markAllSeen(state) {
      state.warningsList.forEach((w) => {
        w.seen = true;
      });
      state.unreadCount = 0;
    },
    openWarnings(state) {
      state.isOpen = true;
      // Update lastOpenedAt and save to localStorage
      const now = new Date().toISOString();
      state.lastOpenedAt = now;
      try {
        localStorage.setItem("warningsLastOpened", now);
      } catch {
        // Ignore localStorage errors
      }
      // Mark all as seen when opened
      state.warningsList.forEach((w) => {
        w.seen = true;
      });
      state.unreadCount = 0;
    },
    closeWarnings(state) {
      state.isOpen = false;
    },
    incrementUnreadCount(state) {
      state.unreadCount += 1;
    },
    removeWarning(state, action: PayloadAction<string>) {
      // Remove warning by ID
      state.warningsList = state.warningsList.filter(
        (w) => w.id !== action.payload
      );
      // Recalculate unread count
      state.unreadCount = state.warningsList.filter((w) => !w.seen).length;
    },
    removeWarningsByTaskId(state, action: PayloadAction<string>) {
      // Remove all warnings related to a specific task ID
      const taskId = action.payload;
      state.warningsList = state.warningsList.filter(
        (w) => !w.id.includes(`task-overdue-${taskId}`) &&
               !w.id.includes(`task-due-soon-${taskId}`)
      );
      // Recalculate unread count
      state.unreadCount = state.warningsList.filter((w) => !w.seen).length;
    },
  },
});

export const {
  addWarning,
  setWarningsList,
  markWarningSeen,
  markAllSeen,
  openWarnings,
  closeWarnings,
  incrementUnreadCount,
  removeWarning,
  removeWarningsByTaskId,
} = warningsSlice.actions;

export default warningsSlice.reducer;
